import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { CrudOperations, OperationKeys } from '@decaf-ts/db-decorators';
import { Comparison, formatDate, isValidDate, Model, Primitives } from '@decaf-ts/decorator-validation';
import { ActionRoles, Dynamic, IconComponent, KeyValue, ModalComponent } from '@decaf-ts/for-angular';
import { IonButton } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { Diffs, ProductImage } from '@pharmaledgerassoc/ptp-toolkit/shared';
import { getExpiryDateDiffValue } from 'src/app/handlers/BatchHandler';
import { isValidBase64 } from 'src/app/utils/helpers';

@Dynamic()
@Component({
  selector: 'app-modal-diffs',
  templateUrl: './modal-diffs.component.html',
  styleUrls: ['./modal-diffs.component.scss'],
  imports: [IconComponent, TranslatePipe, CommonModule, IonButton],
  standalone: true,
})
export class AppModalDiffsComponent extends ModalComponent implements OnInit {
  @Input()
  diffs: Partial<Diffs<Model> | Comparison<Model>> = {};

  @Input()
  message?: string;

  @Input()
  override locale?: string;

  @Input()
  override operation: CrudOperations = OperationKeys.CREATE;

  @Input()
  confirmButton = {
    text: 'continue',
    handle: () => this.handleAction('confirm'),
  };

  @Input()
  customButtons: {
    text: string;
    handle: () => Promise<void>;
  }[] = [];

  filteredDiffs: KeyValue | undefined;

  override async ngOnInit(): Promise<void> {
    this.filteredDiffs = {};
    const entries = Object.entries(this.diffs);
    for (const [k, v] of entries) {
      const propName = k as keyof Model;
      let o = v?.old || v?.other;
      let n = v?.new || v?.current;

      o = await this.parseValue(k, o, this);
      n = await this.parseValue(k, n, this);
      if (typeof o === Primitives.STRING && typeof n === Primitives.STRING) {
        if (o.trim() === n.trim()) {
          continue;
        }
      }
      if (o || n) {
        if (!this.filteredDiffs) {
          this.filteredDiffs = {};
        }
        this.filteredDiffs[propName] = {
          old: o,
          new: n,
        };
      }
    }
    if (!Object.keys(this.filteredDiffs || {})?.length) {
      this.filteredDiffs = undefined;
    }
    await super.ngOnInit();
    this.changeDetectorRef.detectChanges();
  }

  parseLabel(key: string): Promise<string> {
    const [prop] = key.split('_');
    return this.translateService.instant(`${this.locale}.${prop}.label`);
  }

  async parseValue(
    prop: string,
    value: string | object | Date | number | undefined,
    instance: AppModalDiffsComponent
  ): Promise<string | object | undefined> {
    if (prop === 'expiryDate') {
      if (!value) {
        return undefined;
      }
      return await getExpiryDateDiffValue(value as Date | string, instance);
    }
    if (prop === 'imageData' && value) {
      if (typeof value === 'string' && !value?.trim().length) {
        return undefined;
      }
      const { content } = value as ProductImage;
      let image = JSON.parse(content.trim() || '{}');
      if (image) {
        if (Array.isArray(image)) {
          image = image[0];
        }
      }
      if (isValidBase64(image)) {
        const base64Data = JSON.parse((value as ProductImage).content) || undefined;
        if (base64Data) {
          return `<img src="${base64Data?.[0]}" alt="Product Image" style="max-height: 50px;" />`;
        }
      } else {
        return undefined;
      }
    }
    if (typeof value === 'object') {
      const isDate = isValidDate(value);
      if (!isDate && !Object.keys(value).length) {
        return '';
      }
      // return isDate ? formatDate(value as Date, 'dd/MM/yyyy') : value;
      return isDate ? formatDate(value as Date, 'dd/MM/yyyy') : JSON.stringify(value);
    }
    return value === undefined || value === null ? '' : String(value);
  }

  async handleAction(role: 'cancel' | 'confirm' = 'confirm'): Promise<void> {
    if (role === ActionRoles.cancel) {
      return await this.cancel();
    }
    return await this.confirm();
  }
}
